import React, { useEffect, useState } from "react";
import { SelectField, InputField, CheckboxField, FormBase, FormSubmitCallback } from "ui/Form"
import { useManageModal, customfieldOptionType, useSlideInContext } from "ui";
import { FieldProps } from "react-final-form";
import { z } from "zod";
import { useMutation } from "@apollo/client";
import { ADD_CUSTOMFIELD, GET_LOCATION_CUSTOMFIELDS } from "ft-shared-graphql"
import { AddCustomFieldMutation, AddCustomFieldMutationVariables, CustomFieldType } from "ft-shared-graphql";
import { useParams } from "react-router";
import { LocationDataType } from "ft-shared-graphql/types";
const NEW_CUSTOMFIELD_FORM_FIELDS: FieldProps<string, any>[] = [
    {
        name: "type",
        label: "Type",
        required: true,
        fullWidth: true,
        component: SelectField,
        options: customfieldOptionType,
        grid: { xs: 12 }
    },
    {
        name: "name",
        label: "Name",
        required: true,
        fullWidth: true,
        component: InputField,
        grid: { xs: 12 }
    },
    {
        name: "description",
        label: "Description",
        required: true,
        fullWidth: true,
        component: InputField,
        grid: { xs: 12 }
    },
    {
        name: "required",
        label: "Required",
        fullWidth: true,
        component: CheckboxField,
        grid: { xs: 12 }
    }
]
type FormState = {
    fields: FieldProps<string, any>[]
};

const newCustomFieldSchema = z.object({
    type: z.enum(["TEXT", "SELECT"]),
    name: z.string().trim().max(255).min(1),
    description: z.string().trim().max(255).min(1),
    required: z.coerce.boolean().default(false)
})
export type AddCustomFieldProps = {
    locationId: string | null,
    customFields: CustomFieldType[]
}
type NewCustomFormSchema = z.infer<typeof newCustomFieldSchema>
type CustomFormProps = {
    onSubmit: (values: NewCustomFormSchema) => Promise<ReturnType<FormSubmitCallback>>
    onCancel: () => void
}

function NewCustomFieldForm({ onSubmit, onCancel }: CustomFormProps) {
    const [formState, setFormState] = useState<FormState>({ fields: NEW_CUSTOMFIELD_FORM_FIELDS })
    useEffect(() => {
        const fields = [...NEW_CUSTOMFIELD_FORM_FIELDS]
        setFormState(prevState => ({
            ...prevState,
            fields
        }))
    }, [])

    const EditForm = FormBase<NewCustomFormSchema>({
        fields: formState.fields,
        initialValues: {},
        validationSchema: newCustomFieldSchema,
        buttonSubmitLabel: "Save",
        buttonCancelLabel: "Cancel",
        // readonly,
        onSubmit: onSubmit,
        onCancel: onCancel
    })

    return EditForm;
};
export default function AddCustomField({ locationId, customFields }: AddCustomFieldProps) {
    const { id } = useParams()
    const { openModal } = useManageModal()
    const { toggleDrawer } = useSlideInContext()
    const [addCustomField, { error }] = useMutation<AddCustomFieldMutation, AddCustomFieldMutationVariables>(ADD_CUSTOMFIELD, {
        refetchQueries: [GET_LOCATION_CUSTOMFIELDS]
    })
    const handleAddCustomFieldSubmission = async (values: NewCustomFormSchema) => {
        const _position = (customFields?.length ?? 0) > 0 ? 1 : 0
        const input = {
            locationId: locationId ?? id,
            type: values.type as CustomFieldType,
            name: values.name,
            description: values.description,
            placeholder: values.name,
            required: values.required ?? false,
            position: _position
        }
        try {
            const response = await addCustomField({
                variables: { input },
                onError(error, clientOptions) {
                    console.log("query Error: ", error, clientOptions)
                    openModal({ type: "DUPLICATE_FIELD", cancelButtonLabel: "Close" })
                }
            })

            if (response.data) {
                toggleDrawer()
            }
        } catch (err) {
            openModal({ type: "DUPLICATE_FIELD", cancelButtonLabel: "Close" })
        }
    }
    const handleCancel = () => {
        toggleDrawer()
    }
    return (
        <NewCustomFieldForm
            onSubmit={handleAddCustomFieldSubmission}
            onCancel={handleCancel}
        />
    )
}

